import { getAccessToken } from '../auth/storage.js';
import { handleExpiredSession } from '../auth/session.js';
import { restoreScroll } from '../router.js';
import { getChapter, loadTopics } from '../study/content.js';
import { getDoneTopicIds } from '../study/completion.js';
import { backButton } from '../components/branding.js';
import { escapeHtml } from '../utils/html.js';
import { emptyState, errorCard, loaderView, wireRetry } from '../components/feedback.js';
import { renderAppScreen } from '../components/shell.js';
import { topicRow, wireThumbnails } from '../components/topicRow.js';

/**
 * Header block for the chapter: its title and description. The "back" affordance
 * lives in the top bar's leading slot (see {@link renderChapter}), not here.
 */
function chapterHeader(chapter) {
  const title = chapter?.title ? escapeHtml(chapter.title) : 'Rozdział';
  const description = chapter?.description
    ? `<p class="mt-1 text-sm leading-relaxed text-neutral-600">${escapeHtml(chapter.description)}</p>`
    : '';

  return `
    <h1 class="text-2xl font-semibold tracking-tight text-neutral-900">${title}</h1>
    ${description}`;
}

/**
 * Markup for the topics region given the resolved list, the ids of the topics the
 * user has finished (from `topics_done` — the rows themselves say nothing about
 * completion) and any load error.
 */
function topicsRegion(topics, doneIds, error) {
  if (error) return errorCard(error, 'Nie udało się wczytać tematów.');
  if (!topics.length) {
    return emptyState('Brak tematów', 'Ten rozdział nie zawiera jeszcze tematów.');
  }
  return `<ul class="flex flex-col gap-3">${topics
    .map((topic) => topicRow(topic, { done: doneIds.has(topic.id) }))
    .join('')}</ul>`;
}

/**
 * Resolve the chapter's topics (from IndexedDB, fetching only when the cache is
 * empty or the chapter is flagged stale), then render the header and the list.
 */
async function loadChapter(mount, chapterId) {
  const headerEl = mount.querySelector('#chapter-header');
  const topicsEl = mount.querySelector('#topics-region');
  topicsEl.innerHTML = loaderView('Pobieranie lekcji...', { fullscreen: false });

  let chapter = null;
  let topics = [];
  let error = null;
  try {
    ({ chapter, topics } = await loadTopics(getAccessToken(), chapterId));
  } catch (err) {
    if (handleExpiredSession(err)) return;
    error = err;
    // Fall back to whatever header info we have cached for this chapter.
    try {
      chapter = await getChapter(chapterId);
    } catch {
      /* IndexedDB unavailable — leave the header generic */
    }
  }

  // Completion is a separate store, so it is a separate read — and its own
  // failure: an unreadable one costs the checks, not the list.
  let doneIds = new Set();
  try {
    doneIds = await getDoneTopicIds();
  } catch {
    /* IndexedDB unavailable — render the rows without their checks */
  }

  headerEl.innerHTML = chapterHeader(chapter);
  topicsEl.innerHTML = topicsRegion(topics, doneIds, error);
  wireThumbnails(topicsEl, topics);
  restoreScroll(); // the list is in the DOM — return to where the user left it

  wireRetry(topicsEl, () => loadChapter(mount, chapterId));
}

/** Topics screen for a single chapter — reached from a chapter card on home. */
export function renderChapter(mount, chapterId) {
  const content = `
    <div class="flex flex-1 flex-col py-8">
      <header id="chapter-header"></header>
      <section id="topics-region" class="mt-6"></section>
    </div>`;

  renderAppScreen(mount, {
    active: '',
    contentHtml: content,
    leading: backButton(),
    onMount(main) {
      loadChapter(main, chapterId);
    },
  });
}
